/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks';
import { notificationActions, examAssignmentActions } from '../../redux/actions';
import { pathConstants } from '../../constants';

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 20px;
  font-size: 20px;
  color: #0f172a;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ExamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Scroll area to limit visible items while keeping section header static
const ScrollableArea = styled.div`
  max-height: 600px; /* approx 5 items on mobile (single column) */
  overflow-y: auto;
  padding-right: 4px; /* space for scrollbar */

  /* Smooth custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ExamCard = styled.div<{status: string}>`
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(p) => {
      switch (p.status) {
        case 'completed':
          return '#10b981';
        case 'in_progress':
          return '#f59e0b';
        default:
          return '#3b82f6';
      }
    }};
  }

  &:hover {
    border-color: #667eea;
    box-shadow: 0 8px 16px rgba(102, 126, 234, 0.15);
    transform: translateY(-4px);
  }
`;

const ExamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 12px;
`;

const ExamTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const DurationBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 13px;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 3px 8px;
  }
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const StatusBadge = styled.span<{status: string}>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => {
    switch (p.status) {
      case 'completed':
        return '#d1fae5';
      case 'in_progress':
        return '#fef3c7';
      default:
        return '#dbeafe';
    }
  }};
  color: ${(p) => {
    switch (p.status) {
      case 'completed':
        return '#065f46';
      case 'in_progress':
        return '#92400e';
      default:
        return '#1e40af';
    }
  }};
`;

const ActionButton = styled.button<{variant?: 'primary' | 'secondary'}>`
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  white-space: nowrap;

  ${(p) => p.variant === 'secondary' ? `
    background: #e2e8f0;
    color: #1e293b;
    &:hover {
      background: #cbd5e1;
    }
  ` : `
    background: linear-gradient(90deg, #007bff 0%, #00bfff 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
    &:hover {
      box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
      transform: translateY(-2px);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;

  .icon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  .text {
    margin: 0;
    font-size: 16px;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #64748b;

  &::after {
    content: '';
    display: inline-block;
    width: 30px;
    height: 30px;
    border: 4px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface AssignedExam {
  id: number;
  exam: {
    id: number;
    title: string;
    description?: string;
    duration: number;
    share_link?: string;
  };
  status: 'pending' | 'in_progress' | 'completed';
  assigned_at: string;
}

interface AssignedExamsSectionProps {
  userId?: number;
  assignedExams: any[];
  completedExams?: any[];
  loading: boolean;
}

const AssignedExamsSection: React.FC<AssignedExamsSectionProps> = ({
  userId,
  assignedExams,
  completedExams = [],
  loading,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [takingExamId, setTakingExamId] = useState<number | null>(null);

  console.log('AssignedExamsSection - assignedExams:', assignedExams);

  const handleStartExam = (examLink: string) => {
    if (!examLink) {
      dispatch(notificationActions.addNotification('Đề thi không có link', 'DANGER'));
      return;
    }
    // Navigate to exam in current tab for authenticated students
    navigate(`/exam/${examLink}`);
  };

  const handleViewExam = (exam: any) => {
    // Navigate to exam detail page
    window.location.href = `/exam/${exam.share_link}`;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳ Chưa làm';
      case 'in_progress':
        return '⚡ Đang làm';
      case 'completed':
        return '✓ Hoàn thành';
      default:
        return status;
    }
  };

  const isExamCompleted = (examId: number) => {
    return completedExams.some((submission: any) => submission.exam_id === examId);
  };

  const getExamStatus = (assignment: any) => {
    if (isExamCompleted(assignment.exam.id)) {
      return 'completed';
    }
    return assignment.status;
  };

  if (loading) {
    return (
      <SectionCard>
        <SectionTitle>📋 Đề Thi Được Giao</SectionTitle>
        <LoadingSpinner />
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <SectionTitle>📋 Đề Thi Được Giao ({assignedExams.length})</SectionTitle>
      
      {assignedExams.length === 0 ? (
        <EmptyState>
          <div className="icon">📭</div>
          <p className="text">Chưa có đề thi nào được giao cho bạn</p>
        </EmptyState>
      ) : (
        <ScrollableArea>
        <ExamsGrid>
          {assignedExams.map((assignment: AssignedExam) => {
            const exam = assignment.exam;
            if (!exam) {
              console.warn('Assignment has no exam:', assignment);
              return null;
            }
            const computedStatus = getExamStatus(assignment);
            return (
              <ExamCard key={assignment.id} status={computedStatus}>
                <ExamHeader>
                  <ExamTitle>{exam.title}</ExamTitle>
                  {exam.duration && (
                    <DurationBadge>
                      <span>⏱️</span>
                      <span>{exam.duration}p</span>
                    </DurationBadge>
                  )}
                </ExamHeader>
                
                {exam.description && (
                  <div style={{
                    fontSize: '13px',
                    color: '#64748b',
                    marginBottom: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {exam.description}
                  </div>
                )}

                <StatusRow>
                  <StatusBadge status={computedStatus}>
                    {getStatusText(computedStatus)}
                  </StatusBadge>

                  {computedStatus === 'completed' ? (
                    <ActionButton
                      variant="primary"
                      onClick={() => handleStartExam(exam.share_link || '')}
                      disabled={!exam.share_link}
                    >
                      Làm lại
                    </ActionButton>
                  ) : computedStatus === 'in_progress' ? (
                    <ActionButton
                      variant="primary"
                      onClick={() => handleStartExam(exam.share_link || '')}
                      disabled={!exam.share_link}
                    >
                      Tiếp tục
                    </ActionButton>
                  ) : (
                    <ActionButton
                      variant="primary"
                      onClick={() => handleStartExam(exam.share_link || '')}
                      disabled={!exam.share_link}
                    >
                      Bắt đầu
                    </ActionButton>
                  )}
                </StatusRow>
              </ExamCard>
            );
          })}
        </ExamsGrid>
        </ScrollableArea>
      )}
    </SectionCard>
  );
};

export default AssignedExamsSection;
